"use client";

import {
  BanknoteIcon,
  ChevronRight,
  FileTextIcon,
  LucideIcon,
  PlusCircle,
  SmartphoneIcon,
  SparklesIcon,
  WandSparkles,
} from "lucide-react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { ComponentProps, ReactNode, SVGProps } from "react";
import { AdditionalTools } from "./additional-tools-header";
import { imageGeneratorCategories } from "./products/_shared/image-generator-tools";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
  NavigationMenuViewport,
} from "@/components/ui/navigation-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { webAssetsCdnBaseUrl } from "@/shared/constants";
import { HeaderLogo } from "@/shared/header";
import {
  AiClothesChangerIcon,
  AiFaceEditorIcon,
  AiGifGeneratorIcon,
  AiHeadshotGeneratorIcon,
  AiImageGeneratorIcon,
  AiImageUpscalerIcon,
  AiMemeGeneratorIcon,
  AiQrCodeGeneratorIcon,
  AiTalkingPhotoIcon,
  AnimationIcon,
  AutoSubtitleGeneratorIcon,
  FaceSwapPhotoIcon,
  FaceSwapVideoIcon,
  ImageBackgroundRemoverIcon,
  ImageToImageIcon,
  ImageToVideoIcon,
  LipSyncIcon,
  PhotoColorizerIcon,
  TextToVideoIcon,
  VideoToVideoIcon,
} from "@/shared/project-icons";
import { VideoComponent } from "@/shared/video-component";

export type AssetType = "video" | "image";
export interface NavigationProducts {
  name: string;
  icon: LucideIcon;
  navType: "products";
  category: AssetType;
  sublinks: {
    name: string;
    href: string;
    icon: (props: SVGProps<SVGSVGElement>) => ReactNode;
    description: string;
    assetType: "video" | "image";
    badge?: "free";
  }[];
}

const videoNav: NavigationProducts = {
  navType: "products",
  name: "AI Video",
  category: "video",
  icon: WandSparkles,
  sublinks: [
    {
      name: "Face Swap Video",
      href: "/products/face-swap?mode=video",
      icon: FaceSwapVideoIcon,
      description: "Swap faces in any video",
      assetType: "video",
      badge: "free",
    },
    {
      name: "Lip Sync",
      href: "/products/lip-sync",
      icon: LipSyncIcon,
      description: "Sync lips with any audio",
      assetType: "video",
      badge: "free",
    },
    {
      name: "Image-to-Video",
      href: "/products/image-to-video",
      icon: ImageToVideoIcon,
      description: "Turn images into videos",
      assetType: "video",
    },
    {
      name: "Video-to-Video",
      href: "/products/video-to-video",
      icon: VideoToVideoIcon,
      description: "Restyle existing videos",
      assetType: "video",
    },
    {
      name: "Text-to-Video",
      href: "/products/text-to-video",
      icon: TextToVideoIcon,
      description: "Create videos from text",
      assetType: "video",
    },

    {
      name: "Animation",
      href: "/products/animation",
      icon: AnimationIcon,
      description: "Create stop-motion animations",
      assetType: "video",
      badge: "free",
    },
    {
      name: "Subtitle Generator",
      href: "/products/auto-subtitle-generator",
      icon: AutoSubtitleGeneratorIcon,
      description: "Automate video subtitles",
      assetType: "video",
      badge: "free",
    },
    {
      name: "AI Talking Photo",
      href: "/products/ai-talking-photo",
      icon: AiTalkingPhotoIcon,
      description: "Make photos talk",
      assetType: "video",
      badge: "free",
    },
  ],
};

const imageNav: NavigationProducts = {
  navType: "products",
  name: "AI Image",
  category: "image",
  icon: SparklesIcon,
  sublinks: [
    {
      name: "AI Image Generator",
      href: "/products/ai-image-generator",
      icon: AiImageGeneratorIcon,
      description: "Generate stunning images from text",
      assetType: "image",
      badge: "free",
    },
    {
      name: "Face Swap Photo",
      href: "/products/face-swap?mode=photo",
      icon: FaceSwapPhotoIcon,
      description: "Swap faces in any photo",
      assetType: "image",
      badge: "free",
    },
    {
      name: "AI Headshot Generator",
      href: "/products/ai-headshot-generator",
      icon: AiHeadshotGeneratorIcon,
      description: "Create pro headshots instantly",
      assetType: "image",
      badge: "free",
    },
    {
      name: "AI Image Upscaler",
      href: "/products/ai-image-upscaler",
      icon: AiImageUpscalerIcon,
      description: "Upscale and enhance images",
      assetType: "image",
      badge: "free",
    },
    {
      name: "AI Clothes Changer",
      href: "/products/ai-clothes-changer",
      icon: AiClothesChangerIcon,
      description: "Change outfits in photos",
      assetType: "image",
      badge: "free",
    },
    {
      name: "AI QR Code Generator",
      href: "/products/ai-qr-code-generator",
      icon: AiQrCodeGeneratorIcon,
      description: "Generate unique QR codes",
      assetType: "image",
      badge: "free",
    },
    {
      name: "AI Meme Generator",
      href: "/products/ai-meme-generator",
      icon: AiMemeGeneratorIcon,
      description: "Generate memes from text",
      assetType: "image",
      badge: "free",
    },
    {
      name: "AI Face Editor",
      href: "/products/ai-face-editor",
      icon: AiFaceEditorIcon,
      description: "Edit faces in photos",
      assetType: "image",
      badge: "free",
    },
    {
      name: "AI GIF Generator",
      href: "/products/ai-gif-generator",
      icon: AiGifGeneratorIcon,
      description: "Generate GIFs from text and images",
      assetType: "image",
      badge: "free",
    },
    {
      name: "Photo Colorizer",
      href: "/products/photo-colorizer",
      icon: PhotoColorizerIcon,
      description: "Colorize black and white photos",
      assetType: "image",
      badge: "free",
    },
    {
      name: "Background Remover",
      href: "/products/image-background-remover",
      icon: ImageBackgroundRemoverIcon,
      description: "Remove backgrounds instantly",
      assetType: "image",
      badge: "free",
    },
    {
      name: "Image-to-Image",
      href: "/products/image-to-image",
      icon: ImageToImageIcon,
      description: "Restyle existing images",
      assetType: "image",
    },
  ],
};

export function NavMain() {
  const items = [
    videoNav,
    imageNav,
    {
      name: "Pricing",
      icon: BanknoteIcon,
      sublinks: undefined,
      url: "/pricing",
    },
    {
      name: "Blog",
      icon: FileTextIcon,
      sublinks: undefined,
      url: "/blog",
    },
    {
      name: "API",
      icon: SmartphoneIcon,
      sublinks: undefined,
      url: "/api",
    },
  ];

  return (
    <>
      <SidebarGroup>
        <SidebarMenu>
          <SidebarMenuItem>
            <Button asChild>
              <Link href="/create" className="w-full justify-start font-bold">
                <PlusCircle />
                Create New
              </Link>
            </Button>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>
      <SidebarGroup>
        <SidebarMenu>
          {items.map((item, idx) => (
            <Collapsible
              key={item.name}
              asChild
              defaultOpen={idx === 0}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                {item.sublinks ? (
                  <>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip={item.name}>
                        <>
                          <item.icon className={"size-6"} />
                          <span>{item.name}</span>
                          <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </>
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {item.sublinks?.map((subItem) => (
                          <SidebarMenuSubItem key={subItem.name}>
                            <SidebarMenuSubButton asChild>
                              <a href={subItem.href}>
                                <subItem.icon className={"size-6!"} />
                                <span>{subItem.name}</span>
                              </a>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </>
                ) : (
                  <SidebarMenuButton asChild className="flex items-center gap-2">
                    <a href={item.url}>
                      <item.icon className={"size-6"} />
                      <span>{item.name}</span>
                    </a>
                  </SidebarMenuButton>
                )}
              </SidebarMenuItem>
            </Collapsible>
          ))}
        </SidebarMenu>
      </SidebarGroup>
    </>
  );
}

const UserHeader = dynamic(() => import("./user-header"));

export function SignedOutSidebar({ ...props }: ComponentProps<typeof Sidebar>) {
  return (
    <Sidebar {...props}>
      <SidebarHeader>
        <SidebarMenuButton
          size="lg"
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          asChild
        >
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
          <a href="/">
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg">
              <HeaderLogo />
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">Magic Hour</span>
            </div>
          </a>
        </SidebarMenuButton>
      </SidebarHeader>
      <SidebarContent>
        <NavMain />
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
}

export function SignedOutNavigation() {
  return (
    <header className="fixed z-30 h-[68px] w-full text-white" id="header">
      <div className="hidden h-[68px] w-full grid-cols-3 items-center justify-between px-8 py-4 md:grid">
        <Link className="flex items-center space-x-2" href="/">
          <HeaderLogo />
          <span className="text-xl font-bold">Magic Hour</span>
        </Link>

        <NavigationMenu>
          <NavigationMenuList>
            <ProductHeader category={videoNav.category} linkGroup={videoNav} />
            <ProductHeader category={imageNav.category} linkGroup={imageNav} />

            <NavigationMenuItem>
              <Link href="/pricing" legacyBehavior passHref>
                <NavigationMenuLink className={cn(navigationMenuTriggerStyle(), "text-base")}>
                  Pricing
                </NavigationMenuLink>
              </Link>
            </NavigationMenuItem>
            <NavigationMenuItem>
              <Link href="/blog" legacyBehavior passHref>
                <NavigationMenuLink className={cn(navigationMenuTriggerStyle(), "text-base")}>
                  Blog
                </NavigationMenuLink>
              </Link>
            </NavigationMenuItem>
            <NavigationMenuItem>
              <Link href="/api" legacyBehavior passHref>
                <NavigationMenuLink className={cn(navigationMenuTriggerStyle(), "text-base")}>
                  API
                </NavigationMenuLink>
              </Link>
            </NavigationMenuItem>
          </NavigationMenuList>

          <NavigationMenuViewport className="origin-top-center" />
        </NavigationMenu>
        <div className="ml-auto flex gap-1">
          <UserHeader />
        </div>
      </div>

      <div className="grid h-[68px] w-full grid-cols-3 px-4 py-4 md:hidden">
        <SignedOutSidebar />

        <header className="flex shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2">
            <SidebarTrigger />
          </div>
        </header>

        <div className="flex items-center justify-center space-x-2">
          <HeaderLogo />
        </div>

        <div className="flex justify-end gap-1">
          <UserHeader />
        </div>
      </div>
    </header>
  );
}

function ProductHeader({
  linkGroup,
  category,
}: {
  linkGroup: NavigationProducts;
  category: AssetType;
}) {
  const { sublinks } = linkGroup;

  const itemCount = 4;

  const links =
    category === "video"
      ? sublinks
      : [
          ...sublinks,
          ...imageGeneratorCategories.map((c) => {
            return {
              name: c.title,
              badge: "free" as const,
              assetType: "image",
              href: `/tools/${c.tool}`,
              description: "string",
              icon: c.icon,
            };
          }),
        ];

  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger>
        <span className="capitalize">
          <span className="hidden xl:inline-block">AI&nbsp;</span>
          {category}
        </span>
      </NavigationMenuTrigger>
      <NavigationMenuContent className="w-[900px] md:w-[700px] lg:w-[1000px] xl:w-[1200px]">
        <div className="max-h-[calc(100vh-100px)] w-full space-y-6 overflow-y-scroll rounded-lg p-6 shadow-lg ring-1 ring-gray-900/5">
          <h3 className="mb-4 text-lg font-semibold text-neutral-50 capitalize">
            AI {category} Tools
          </h3>
          <div className={cn(`grid grid-cols-${itemCount} gap-4`)}>
            {links.slice(0, itemCount).map((item) => {
              const src = item.href
                .replace("/products/", "")
                .replace("face-swap?mode=video", "faceswap")
                .replace("face-swap?mode=photo", "face-swap-photo");
              return (
                <Link key={item.name} href={item.href} className="block max-w-64 min-w-48">
                  <div className="group relative w-full max-w-64 min-w-48 overflow-hidden rounded-xl bg-neutral-900">
                    <div className="aspect-video w-full overflow-hidden">
                      <div className="relative h-full w-full">
                        {category === "video" ? (
                          <VideoComponent
                            assetPath={`projects/${src}-landing-page`}
                            muted
                            autoPlay
                            loop
                            playsInline
                            className="block aspect-video h-full w-full rounded-md object-cover"
                          />
                        ) : (
                          <picture className="">
                            <source
                              srcSet={`${webAssetsCdnBaseUrl}/projects/${src}-landing-page.webp`}
                              type="image/webp"
                            />
                            <img
                              src={`${webAssetsCdnBaseUrl}/projects/${src}-landing-page.jpeg`}
                              className="block aspect-video h-full w-full rounded-md object-cover"
                              alt={src}
                            />
                          </picture>
                        )}
                        <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/20 to-transparent" />
                      </div>
                    </div>

                    <div className="absolute right-0 bottom-0 left-0 flex flex-col gap-1 p-2">
                      {item.badge && <Badge variant="default">Free</Badge>}
                      <h3 className="text-base leading-tight font-medium text-white">
                        {item.name}
                      </h3>
                      <p className="text-sm leading-tight text-neutral-300">{item.description}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {links.length > itemCount && (
            <AdditionalTools
              links={links.slice(itemCount)}
              title={`Additional ${category} Tools`}
            />
          )}
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
}
